// src/components/Admin/QuestionnaireTemplateListPage.js
// FINAL VERSION with QUERY FIX - June 29, 2025

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { collection, query, onSnapshot, orderBy, addDoc, updateDoc, doc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import {
    Box, Paper, Typography, Button, TextField, Select, MenuItem,
    Table, TableContainer, TableHead, TableRow, TableCell, TableBody,
    IconButton, Menu, InputLabel, FormControl,
    Dialog, DialogTitle, DialogContent, Divider, DialogActions, DialogContentText
} from '@mui/material';
import { Add as AddIcon, MoreVert as MoreVertIcon, Warning as WarningIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import CreateEditQuestionnaireTemplatePage from '../../pages/admin/CreateEditQuestionnaireTemplatePage';


const QuestionnaireTemplateListPage = () => {
    // --- STATE MANAGEMENT ---
    const [templates, setTemplates] = useState([]);
    const [_loading, setLoading] = useState(true);
    const [_error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    
    const [modalMode, setModalMode] = useState(null);
    const [templateToEdit, setTemplateToEdit] = useState(null);

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);


    // --- DATA FETCHING ---
    useEffect(() => {
        // FIX: Order by 'name' to ensure all documents are returned, even if they are missing 'lastUpdated'.
        const q = query(collection(db, 'questionnaireTemplates'), orderBy('name', 'asc'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const templatesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                lastUpdated: doc.data().lastUpdated?.toDate()
            }));
            setTemplates(templatesData);
            setLoading(false);
        }, (err) => {
            console.error("Error fetching questionnaire templates:", err);
            setError("Failed to load questionnaire templates.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // --- FILTERING LOGIC ---
    const filteredTemplates = useMemo(() => {
        if (!templates) return [];
        return templates.filter(template => {
            const name = template.name || '';
            const version = template.version || '';
            const status = template.status || '';

            const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                  version.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesStatus = statusFilter === 'All' || status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [templates, searchQuery, statusFilter]);


    // --- HANDLERS ---
    const handleMenuOpen = (event, template) => {
        setAnchorEl(event.currentTarget);
        setSelectedTemplate(template);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedTemplate(null);
    };
    
    const handleOpenModal = (mode, template = null) => {
        setModalMode(mode);
        setTemplateToEdit(template);
        handleMenuClose();
    };

    const handleCloseModal = () => {
        setModalMode(null);
        setTemplateToEdit(null);
    };

    const handleSaveTemplate = async (templateData) => {
        setLoading(true);
        try {
            if (modalMode === 'edit' && templateToEdit?.id) {
                const templateRef = doc(db, "questionnaireTemplates", templateToEdit.id);
                await updateDoc(templateRef, { ...templateData, lastUpdated: serverTimestamp() });
                toast.success(`Template "${templateData.name}" updated successfully!`);
            } else {
                await addDoc(collection(db, "questionnaireTemplates"), { ...templateData, createdAt: serverTimestamp(), lastUpdated: serverTimestamp() });
                toast.success(`Template "${templateData.name}" created successfully!`);
            }
            handleCloseModal();
        } catch (e) {
            console.error("Error saving document: ", e);
            toast.error("Failed to save template. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDuplicate = async () => {
        if (!selectedTemplate) return;
        setLoading(true);
        const { id, ...originalData } = selectedTemplate;
        const newTemplateData = { ...originalData, name: `Copy of ${originalData.name}`, status: 'Draft', createdAt: serverTimestamp(), lastUpdated: serverTimestamp() };
        try {
            await addDoc(collection(db, "questionnaireTemplates"), newTemplateData);
            toast.success(`Successfully duplicated "${originalData.name}".`);
        } catch (e) {
            console.error("Error duplicating document: ", e);
            toast.error("Failed to duplicate template.");
        } finally {
            setLoading(false);
            handleMenuClose();
        }
    };
    
    // --- UPDATED: Delete handler logic fixed ---
    const handleDelete = () => {
        setAnchorEl(null); // Close the menu, but keep the template selected
        setIsDeleteConfirmOpen(true);
    };

    const handleCancelDelete = () => {
        setIsDeleteConfirmOpen(false);
        setSelectedTemplate(null); // Clear selection on cancel
    };

    const confirmDelete = async () => {
        if (!selectedTemplate) return;
        setLoading(true);
        try {
            await deleteDoc(doc(db, "questionnaireTemplates", selectedTemplate.id));
            toast.success(`Template "${selectedTemplate.name}" was permanently deleted.`);
        } catch (e) {
            console.error("Error deleting document: ", e);
            toast.error("Failed to delete template.");
        } finally {
            setLoading(false);
            setIsDeleteConfirmOpen(false); // Close the dialog
            setSelectedTemplate(null); // Clear selection
        }
    };


    // --- RENDER LOGIC ---
    return (
        <Paper sx={{ p: 3 }} elevation={2}>
            {/* ... (rest of the JSX is unchanged) ... */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}><Typography variant="h5">Questionnaire Templates</Typography><Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal('create')}>Create New Template</Button></Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}><TextField label="Search Templates" variant="outlined" size="small" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ flexGrow: 1, minWidth: '200px' }}/><FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Status</InputLabel><Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}><MenuItem value="All">All</MenuItem><MenuItem value="Draft">Draft</MenuItem><MenuItem value="Active">Active</MenuItem><MenuItem value="Archived">Archived</MenuItem></Select></FormControl></Box>
            <TableContainer><Table><TableHead><TableRow><TableCell>Template Name</TableCell><TableCell>Version</TableCell><TableCell>Status</TableCell><TableCell># of Questions</TableCell><TableCell>Last Updated</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                <TableBody>
                    {filteredTemplates.map((template) => (
                        <TableRow key={template.id} hover>
                            <TableCell sx={{ fontWeight: 'medium' }}>{template.name}</TableCell>
                            <TableCell>{template.version}</TableCell>
                            <TableCell>{template.status}</TableCell>
                            <TableCell>{template.questions?.length || 0}</TableCell>
                            <TableCell>{template.lastUpdated ? template.lastUpdated.toLocaleString() : 'N/A'}</TableCell>
                            <TableCell align="right"><IconButton onClick={(e) => handleMenuOpen(e, template)}><MoreVertIcon /></IconButton></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table></TableContainer>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={() => handleOpenModal('edit', selectedTemplate)}>View / Edit</MenuItem>
                <MenuItem onClick={handleDuplicate}>Duplicate</MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>Delete</MenuItem>
            </Menu>
            <Dialog open={modalMode === 'create' || modalMode === 'edit'} onClose={handleCloseModal} fullWidth maxWidth="lg">
                <DialogTitle>{modalMode === 'edit' ? 'Edit Questionnaire Template' : 'Create New Questionnaire Template'}</DialogTitle><Divider />
                <DialogContent>
                    <CreateEditQuestionnaireTemplatePage template={templateToEdit} onSave={handleSaveTemplate} onClose={handleCloseModal}/>
                </DialogContent>
            </Dialog>

            {/* --- UPDATED: Delete Confirmation Dialog with corrected handlers --- */}
            <Dialog open={isDeleteConfirmOpen} onClose={handleCancelDelete}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                    <WarningIcon color="error" sx={{ mr: 1 }} />
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to permanently delete the template "{selectedTemplate?.name}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default QuestionnaireTemplateListPage;